/**
 * راوتر صغير جوّه المشروع — بدل مكتبة خارجية.
 * السيستم كله شاشات جوّه شِل واحد، فمحتاجين مسار + تنقّل + رجوع بالمتصفح وبس.
 *
 * الملف ده كمان بيحمل **جدول المسارات** — مصدر واحد لكل شاشة في السيستم:
 * مسارها · عنوانها · مين يوصلها · بتتلف في الشِل ولا لأ.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type RouterValue = {
  path: string;
  /** الاستعلام كامل مع علامة الاستفهام، أو نص فاضي */
  search: string;
  /** بارامتر واحد من الاستعلام — للوصول للمهمة اللي الإشعار بيشاور عليها */
  param: (name: string) => string | null;
  navigate: (to: string, opts?: { replace?: boolean }) => void;
};

const RouterCtx = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  // ⚠️ المسار والاستعلام **منفصلين**. قبل كده كان `navigate("/desk?task=9")`
  //    بيحط النص كله في `path`، فما يطابقش أي شاشة ⇒ الموظف اللي بيضغط
  //    إشعار بيهبط على «الصفحة مش موجودة» بدل ما يوصل للمهمة نفسها.
  const [path, setPath] = useState(() => window.location.pathname || "/");
  const [search, setSearch] = useState(() => window.location.search || "");

  useEffect(() => {
    const onPop = () => {
      setPath(window.location.pathname || "/");
      setSearch(window.location.search || "");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string, opts?: { replace?: boolean }) => {
    const qi = to.indexOf("?");
    const nextPath = (qi === -1 ? to : to.slice(0, qi)) || "/";
    const nextSearch = qi === -1 ? "" : to.slice(qi);
    const current = (window.location.pathname || "/") + (window.location.search || "");
    if (to === current) return;
    if (opts?.replace) window.history.replaceState(null, "", to);
    else window.history.pushState(null, "", to);
    setPath(nextPath);
    setSearch(nextSearch);
  }, []);

  /** بارامتر من الاستعلام — ده اللي بيخلّي رابط الإشعار يوصل للمهمة بعينها */
  const param = useCallback(
    (name: string): string | null => new URLSearchParams(search).get(name),
    [search],
  );

  const value = useMemo(() => ({ path, search, param, navigate }), [path, search, param, navigate]);
  return <RouterCtx.Provider value={value}>{children}</RouterCtx.Provider>;
}

export function useRouter(): RouterValue {
  const ctx = useContext(RouterCtx);
  if (!ctx) throw new Error("useRouter لازم يتنده جوّه RouterProvider");
  return ctx;
}

/** رابط داخلي — بيمنع إعادة تحميل الصفحة كلها */
export function Link({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/* ══════════════════════════════════════════════════════════════════
   جدول المسارات
   ══════════════════════════════════════════════════════════════════

   ⚠️ **الشاشة اللي في التصميم لازم يكون ليها مسار من دلوقتي** — حتى لو
   الكود بتاعها لسه ما اتكتبش. من غير كده الموظف اللي بيدوس على عنصر في
   قائمة التنقّل أو على الجرس بيوقع على «الصفحة دي مش موجودة» — وده كذب:
   الشاشة موجودة ومعتمدة، هي بس لسه بتتجهّز. المسار الناقص بيتعرض عليه
   `UnderConstruction` مش `NotFound`.

   إزاي تضيف شاشتك من غير ما تلمس الراوتنج:
   حط ملفك في `src/screens/<Screen>.tsx` وصدّر منه `export function <Screen>()`
   بنفس اسم عمود `screen` تحت. `App.tsx` بيلقطه لوحده وقت البناء.
*/

/** مين له حق يوصل المسار */
export type RouteAccess =
  /** زائر مش مسجّل دخول (الدخول · التسجيل) */
  | "guest"
  /** حساب اتسجّل ولسه مستني تأكيد البريد أو اعتماد الإدارة */
  | "pending"
  /** حساب معتمد وشغّال */
  | "active"
  /**
   * 🔑 **مفتوح للكل** — الشاشات اللي بيتوصلها من رابط في إيميل.
   *
   * 🔴 **ليه اتضاف (٢٠٢٦-٠٩-١٩):** رابط تأكيد البريد بيوصل لحد ممكن يكون
   *    خارج، أو داخل ومستني اعتماد، أو داخل عادي. ولو الصفحة اتحطّت
   *    `guest`، الحساب المعلّق اللي يدوس الرابط بيترمي على «تحت المراجعة»
   *    **من غير ما يتأكّد بريده أبدًا** — يعني يفضل عالق للأبد.
   *    والمستويات التلاتة القديمة كلها بترمي واحد من التلاتة بره.
   */
  | "open";

/** الشاشة بتتلف في الشِل (سايدبار + شريط علوي) ولا بتقف لوحدها */
export type RouteChrome = "shell" | "bare";

export type RouteDef = {
  path: string;
  /** اسم الملف والتصدير المتوقّعين جوّه `src/screens` */
  screen: string;
  /** العنوان في الشريط العلوي — وكمان نص «لسه بتتجهّز» */
  title: string;
  chrome: RouteChrome;
  access: RouteAccess;
  /** المرحلة اللي الشاشة مخطّطة فيها — للتوثيق وترتيب الشغل */
  phase: 1 | 2;
};

export const LOGIN_PATH = "/login";
export const REGISTER_PATH = "/register";
export const PENDING_PATH = "/pending";
/** ⛔ القيم دي **بتتبعت في إيميلات** — تغييرها بيكسر كل رابط اتبعت قبل كده. */
export const VERIFY_EMAIL_PATH = "/verify-email";
export const SET_PASSWORD_PATH = "/set-password";
/** أول شاشة بعد الدخول */
export const HOME_PATH = "/desk";

export const ROUTES: readonly RouteDef[] = [
  // ── المصادقة: بلا شِل ──
  { path: LOGIN_PATH,    screen: "Login",    title: "تسجيل الدخول",          chrome: "bare", access: "guest",   phase: 1 },
  { path: REGISTER_PATH, screen: "Register", title: "تسجيل حساب جديد",       chrome: "bare", access: "guest",   phase: 1 },
  // ⛔ الحساب المعلّق ما بيشوفش الشِل أصلًا — شاشة واحدة وزرار خروج
  { path: PENDING_PATH,  screen: "Pending",  title: "تحت المراجعة",          chrome: "bare", access: "pending", phase: 1 },
  // 🔴 المسارين دول كانوا **ناقصين خالص** والباك إند بيبعتهم في الإيميل من
  //    أول يوم. النتيجة: رابط التأكيد ورابط استعادة كلمة السر الاتنين
  //    كانوا بيرموا الموظف على شاشة الدخول، فيفتكر إنه غلط ويحاول لحد ما
  //    حسابه يتقفل. تست `test_email_links_have_screens` بيحرس ده دلوقتي.
  { path: VERIFY_EMAIL_PATH, screen: "VerifyEmail", title: "تأكيد البريد",     chrome: "bare", access: "open",    phase: 1 },
  { path: SET_PASSWORD_PATH, screen: "SetPassword", title: "تحديد كلمة السر",  chrome: "bare", access: "open",    phase: 1 },

  // ── المرحلة الأولى ──
  { path: HOME_PATH,        screen: "Desk",          title: "مكتبي",                   chrome: "shell", access: "active", phase: 1 },
  { path: "/team",          screen: "Team",          title: "فريقي",                   chrome: "shell", access: "active", phase: 1 },
  // 🔁 تبويب «المهام المتكررة» جوّه نفس الشاشة. مسار حقيقي عشان الرجوع
  //    بالمتصفح والرابط المباشر يشتغلوا — و`Team` هي اللي بتقرر أنهي تبويب
  //    تعرض. ⚠️ مش عنصر في قائمة التنقّل عن قصد: القايمة ثابتة لكل الأدوار،
  //    والتبويب ده للمنسق والمدير بس — عنصر ظاهر لعضو ما يقدرش يفتحه هو
  //    نفس عطل «الصفحة مش موجودة» اللي اتقفل قبل كده.
  { path: "/team/recurring", screen: "Team",         title: "المهام المتكررة",         chrome: "shell", access: "active", phase: 1 },
  { path: "/leads",         screen: "Leads",         title: "الليدز",                  chrome: "shell", access: "active", phase: 1 },
  { path: "/notifications", screen: "Notifications", title: "الإشعارات",               chrome: "shell", access: "active", phase: 1 },
  { path: "/access",        screen: "Access",        title: "المستخدمون والصلاحيات",   chrome: "shell", access: "active", phase: 1 },
  // ⚠️ المسارَان دول بيخدموا **القايمة والسجل مع بعض**: `matchRoute` بتطابق
  //    بالبادئة، فـ`/students/9f2` بتوصل لنفس الشاشة وهي اللي بتقرر تعرض
  //    القايمة ولا سجل الفرد. من غير كده كل ضغطة على صف كانت هتوقّع الموظفة
  //    على «الصفحة دي مش موجودة».
  { path: "/students",      screen: "Students",      title: "سجل الطلاب",              chrome: "shell", access: "active", phase: 1 },
  { path: "/teachers",      screen: "Teachers",      title: "سجل المعلمين",            chrome: "shell", access: "active", phase: 1 },

  // ── باقي عناصر قائمة التنقّل: معرّفة من دلوقتي عشان مفيش عنصر يوقّع على 404 ──
  // 🔑 «التقييم والترشيح» و«الجروبات» شاشتين شغّالتين دلوقتي — الحجز
  //    والترشيح وإنشاء الجروب. `/groups` مالهاش عنصر تنقّل عمدًا (قايمة
  //    التنقّل الـ13 منسوخة من التصميم) — بيتوصلها من كارت الاشتراكات
  //    المستنية ومن رابط الرحلة.
  { path: "/groups",      screen: "Groups",           title: "الجروبات وحزمة التهيئة", chrome: "shell", access: "active", phase: 2 },
  { path: "/journeys",    screen: "Journeys",         title: "الرحلات",            chrome: "shell", access: "active", phase: 1 },
  // 🔒 مكتب الحسابات — الشاشة بتفتح لكل حد، والسيرفر هو اللي بيرفض
  //    (403) للقسم اللي مش من قرّاء الفلوس. الإخفاء في الواجهة بس بيبقى
  //    وهم أمان: المسار موجود واللي يعرفه بيفتحه.
  { path: "/accounts",    screen: "Accounts",         title: "مكتب الحسابات",      chrome: "shell", access: "active", phase: 1 },
    // ⚠️ الشاشتان دول **مبنيتان بحالة موقوفة صادقة** مش `UnderConstruction`:
  //    بيقولوا إيه اللي واقفهم بالظبط وإيه البديل دلوقتي. الفرق مهم —
  //    «لسه بتتجهّز» ما بيقولش للموظف يعمل إيه النهارده.
  { path: "/assistant",   screen: "Assistant",        title: "المساعد",            chrome: "shell", access: "active", phase: 1 },
  { path: "/evaluation",  screen: "Evaluation",       title: "التقييم والترشيح",   chrome: "shell", access: "active", phase: 2 },
  // 🔴 إتاحات المقيّمات — كوليكشن `availability` كان موجودًا وفاضيًا من يوم
  //    ما اتعمل، وشاشة الحجز مكتوب فيها «الموعد بيتكتب بإيدك». دي الشاشة
  //    اللي بتملاه، وبتوري لدعم الطلاب مين فاضي إمتى وبيقيّم إيه.
  { path: "/evaluation/availability", screen: "Availability", title: "إتاحات التقييم", chrome: "shell", access: "active", phase: 2 },
  { path: "/supervision", screen: "AdminSupervision", title: "الإشراف الإداري",    chrome: "shell", access: "active", phase: 1 },
  { path: "/hiring",      screen: "Hiring",           title: "التوظيف والتدريب",   chrome: "shell", access: "active", phase: 1 },
  { path: "/recordings",  screen: "Recordings",       title: "التسجيلات",          chrome: "shell", access: "active", phase: 1 },
  { path: "/director",    screen: "Director",         title: "لوحة المدير",        chrome: "shell", access: "active", phase: 2 },
  // 📊 لوحة الأداء — بتقيس المخرجات. مفتوحة لكل موظف: الرقم اللي بيتحاسب
  //    عليه لازم يكون قدامه هو، مش سرًّا عند مديره.
  { path: "/performance", screen: "Performance",      title: "الأداء",             chrome: "shell", access: "active", phase: 2 },
];

/** بيشيل السلاش الزيادة في الآخر — «/team/» و«/team» نفس المسار */
function normalizePath(raw: string): string {
  const p = raw || "/";
  if (p.length > 1 && p.endsWith("/")) return p.slice(0, -1);
  return p;
}

/**
 * بيلاقي تعريف المسار.
 * التطابق الكامل الأول، وبعدين البادئة عشان المسارات الفرعية
 * (`/leads/9f2` بتفضل جوّه شاشة الليدز).
 */
export function matchRoute(rawPath: string): RouteDef | null {
  const path = normalizePath(rawPath);
  const exact = ROUTES.find((r) => r.path === path);
  if (exact) return exact;
  // الأطول الأول عشان مسار بادئته أقصر ما يخطفش مسار أطول
  const prefixed = ROUTES.filter((r) => path.startsWith(`${r.path}/`)).sort(
    (a, b) => b.path.length - a.path.length,
  );
  return prefixed[0] ?? null;
}

/* ══════════════════════════════════════════════════════════════════
   قرار التوجيه — دالة نقية عشان تتجرّب لوحدها
   ══════════════════════════════════════════════════════════════════ */

/** حالة الجلسة زي ما `SessionProvider` بيرجّعها */
export type SessionGate = "loading" | "offline" | "guest" | "authed";

/**
 * بيرجّع المسار اللي المفروض نتحوّل له، أو `null` يعني «قعّده مكانه».
 *
 * ⚠️ «بيحمّل» و«مفيش اتصال» بيرجّعوا `null` عمدًا: انقطاع الشبكة **مش**
 *    تسجيل خروج. لو حوّلنا فيهم، الموظف بيتطرد لشاشة الدخول لمجرد إن
 *    السيستم غمز لحظة — وشاشة الدخول نفسها هتفشل لأن السيستم واقع.
 */
/**
 * هل المسار ده مسموح للحالة دي؟ — **المصدر الوحيد للقرار**.
 *
 * 🔴 **ليه اتعملت (٢٠٢٦-٠٩-١٩):** القرار كان متكرر في مكانين — `decideRedirect`
 *    هنا، وتلات فروع في `App.tsx` بتقارن `route.access` بإيدها. أول ما
 *    اتضاف مستوى `open` اتزوّد في مكان واحد بس، فالراوتر قال «ما تحوّلوش»
 *    والـApp قال «ما ترسموش» ⇒ **شاشة تحميل للأبد** على رابط التأكيد.
 *    ⚠️ والتست عدّى لأنه كان بيقرا `router.tsx` بس — تطبيقان لمنطق واحد
 *       بيدّوا سلوكًا غلط مش تست أحمر.
 */
export function routeAllows(
  access: RouteAccess,
  viewer: "guest" | "pending" | "active",
): boolean {
  // «مفتوح» بيوصله الزائر واللي مستني اعتماد واللي داخل عادي — لأنه جاي
  // من رابط في إيميل، ومفيش طريقة نعرف بيها حالة اللي هيدوسه.
  if (access === "open") return true;
  return access === viewer;
}

export function decideRedirect(input: {
  status: SessionGate;
  /** الحساب مستني تأكيد بريد أو اعتماد إدارة */
  pending: boolean;
  path: string;
}): string | null {
  const { status, pending, path } = input;
  if (status === "loading" || status === "offline") return null;

  const route = matchRoute(path);

  if (status === "guest") {
    return route && routeAllows(route.access, "guest") ? null : LOGIN_PATH;
  }

  // ⛔ الحساب المعلّق: شاشة واحدة بلا استثناء — صفر بيانات وصفر مسارات
  if (pending) {
    // ⚠️ «مفتوح» بيعدّي هنا عن قصد: اللي مستني تأكيد بريده لازم يقدر
    //    يفتح رابط التأكيد. من غير الاستثناء ده بيبقى عالق للأبد.
    return route && routeAllows(route.access, "pending") ? null : PENDING_PATH;
  }

  // الجذر مش شاشة
  if (path === "/" || path === "") return HOME_PATH;
  // شاشات المصادقة والانتظار مالهاش لازمة لحساب مفعّل
  // ⚠️ و«مفتوح» بتفضل شغّالة للحساب النشط كمان: اللي بيغيّر كلمة سره من
  //    رابط وهو داخل لازم يوصل الصفحة مش يترمي على مكتبه.
  if (route && !routeAllows(route.access, "active")) return HOME_PATH;
  // ⚠️ الرابط الغلط ما بيتحوّلش — بيوقف على «مش موجودة» عشان يبان
  return null;
}
